import React, { forwardRef, InputHTMLAttributes } from 'react';
import cx from 'classnames';
import { Search } from 'lucide-react';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** `compact` filters a list in a side column; `large` is the search on a phone, icon first. */
  variant?: 'compact' | 'large';
  /** Replaces the magnifier. */
  icon?: React.ReactNode;
  /** Layout of the wrapper: margins, width. */
  className?: string;
  'aria-label': string;
}

const looks = {
  compact: {
    input: 'rounded-control border border-line py-2 pl-3 pr-8 text-ui outline-none focus:border-ink-subtle',
    icon: 'right-3',
  },
  large: {
    input:
      'min-h-12 rounded-card border border-line bg-surface py-3 pl-11 pr-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
    icon: 'left-4',
  },
};

/** A search box over a list: quieter than a form field — no label, no red focus frame. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ variant = 'compact', icon, className, ...rest }, ref) => (
    <div className={cx('relative', className)}>
      <input
        ref={ref}
        type="search"
        autoComplete="off"
        className={cx('w-full text-ink placeholder:text-ink-subtle', looks[variant].input)}
        {...rest}
      />
      <span
        className={cx(
          'pointer-events-none absolute top-1/2 flex -translate-y-1/2 text-ink-subtle',
          looks[variant].icon,
        )}
        aria-hidden
      >
        {icon ?? <Search size={14} />}
      </span>
    </div>
  ),
);
SearchInput.displayName = 'SearchInput';
